import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Clock, Home, Plane } from "lucide-react";
import { HERO_IMAGE } from "@/data/portfolioData";

const STATS = [
  { icon: Home, value: "Interior + Exterior", label: "Complete property coverage", testid: "hero-stat-listings-count" },
  { icon: Plane, value: "Aerial Drone", label: "Bird's-eye photos that sell the location", testid: "hero-stat-property-value" },
  { icon: Clock, value: "Fast Delivery", label: "MLS-ready images, quick turnaround", testid: "hero-stat-turnaround" },
];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const [introDone, setIntroDone] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

  return (
    <>
      <AnimatePresence>
        {!introDone && (
          <motion.div
            data-testid="intro-overlay"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0A0A0C]"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.7, ease: "easeInOut" } }}
          >
            <motion.p
              initial={{ opacity: 0, letterSpacing: "0.15em" }}
              animate={{ opacity: 1, letterSpacing: "0.55em" }}
              transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
              onAnimationComplete={() => setTimeout(() => setIntroDone(true), 320)}
              className="font-display px-6 text-center text-xs font-bold uppercase text-[#E2C275] sm:text-base"
            >
              Coastal Choice Solutions
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <section id="top" ref={ref} data-testid="hero-section-container" className="grain-overlay relative flex min-h-screen items-end overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <motion.img
            src={HERO_IMAGE}
            alt="Home photographed by Coastal Choice Solutions"
            initial={{ scale: 1.14 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/40 via-[#0A0A0C]/30 to-[#0A0A0C]" />

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-40 sm:px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.35 }}
            className="eyebrow-label mb-6"
          >
            Real Estate Photography · Astoria, Oregon
          </motion.p>

          <h1
            data-testid="hero-main-title"
            className="font-display max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tight text-zinc-50 sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={1.5}>Listings that stop</MaskedLine>
            <MaskedLine delay={1.62}>the scroll.</MaskedLine>
            <MaskedLine delay={1.78} className="text-gold-gradient">
              Photos that sell the home.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.0 }}
            data-testid="hero-subtitle"
            className="mt-6 max-w-xl text-base text-zinc-300 leading-relaxed"
          >
            Interior, exterior, and aerial drone photography for homes across Astoria and the
            Oregon Coast — crisp, bright, MLS-ready images delivered fast.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#albums" data-testid="hero-cta-explore-albums" className="btn-gold">
              Explore the Albums <ArrowDown size={16} />
            </a>
            <a href="#contact" data-testid="hero-cta-get-quote" className="btn-ghost-lux">
              Get a Quote
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.3 }}
            className="mt-16 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3"
          >
            {STATS.map((s) => (
              <div
                key={s.label}
                data-testid={s.testid}
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 backdrop-blur-md"
              >
                <s.icon size={22} className="shrink-0 text-[#D4AF37]" />
                <div>
                  <div className="font-display text-xl font-bold text-zinc-50">{s.value}</div>
                  <div className="text-xs text-zinc-400">{s.label}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>
    </>
  );
};

export default Hero;
